import { CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import React from 'react';

import { WorkflowStatusChip } from '@/sections/insights/aiAssistant/components/WorkflowStatusChip';
import {
  isActive,
  isFail,
  isPass,
  isRunning,
} from '@/sections/insights/aiAssistant/utils/jobLedger.util';

interface ConversationJobsProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  jobs: any[];
  isLoading?: boolean;
}

type JobBucket = 'active' | 'completed';

// The job's target hostname is the backend-stamped top-level `dns` field
// (set from the target entity's Group()). When it's empty (e.g. a pre-keyed
// job), fall back to the target entity's own dns/name: the target serializes
// as { model: <entity>, type } (registry.Wrapper) — older payloads used a
// { Target: <entity> } key, so check both. Returns undefined so the caller
// omits the line rather than render a placeholder.
//
// Deliberately does NOT read `<entity>.source` (provenance: "self"/"account"/
// "seed"/a capability name like "hannibal", not an address — the original bug),
// and does NOT split the job key: segment ordering is inconsistent across the
// codebase (#job#<dns>#<id>#<capability> vs other formats), so it is unreliable.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getTargetLabel(job: any): string | undefined {
  if (typeof job?.dns === 'string' && job.dns !== '') return job.dns;
  const entity = job?.target?.model ?? job?.target?.Target;
  const fromEntity = entity?.dns || entity?.name;
  if (typeof fromEntity === 'string' && fromEntity !== '') return fromEntity;
  return undefined;
}

// Completed jobs expose `created`/`updated` ISO timestamps; the elapsed time
// between them is the run duration. Returns undefined when either is missing
// or unparseable so the duration line can be omitted.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getDurationLabel(job: any): string | undefined {
  const created = job?.created;
  const updated = job?.updated;
  if (typeof created !== 'string' || typeof updated !== 'string') {
    return undefined;
  }
  const start = new Date(created).getTime();
  const end = new Date(updated).getTime();
  if (isNaN(start) || isNaN(end) || end < start) return undefined;
  const ms = end - start;
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

// Left status icon (16px slot). Running = pulsing brand dot; queued = hollow
// ring; pass = check (success-check color); fail = X (error color).
function JobStatusIcon({ status }: { status: unknown }) {
  if (isPass(status)) {
    return <CheckIcon className="text-chariot-stroke-light-green size-4" />;
  }
  if (isFail(status)) {
    return <XMarkIcon className="text-chariot-stroke-critical size-4" />;
  }
  if (isRunning(status)) {
    return (
      <span className="bg-chariot-brand-primary size-2.5 animate-pulse rounded-full" />
    );
  }
  // queued
  return (
    <span className="border-chariot-stroke size-2.5 rounded-full border-2" />
  );
}

// Maps a backend job status to the shared chip + its Jobs-ledger label.
function JobStatusChip({ status }: { status: unknown }) {
  if (isPass(status)) {
    return <WorkflowStatusChip status="pass" label="Complete" />;
  }
  if (isFail(status)) {
    return <WorkflowStatusChip status="fail" label="Failed" />;
  }
  if (isRunning(status)) {
    return <WorkflowStatusChip status="running" label="Running" />;
  }
  return <WorkflowStatusChip status="queued" label="Queued" />;
}

interface JobCardProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  job: any;
  bucket: JobBucket;
}

function JobCard({ job, bucket }: JobCardProps) {
  const isDone = bucket === 'completed';
  const target = getTargetLabel(job);
  const duration = isDone ? getDurationLabel(job) : undefined;

  return (
    <div
      className={
        isDone
          ? 'bg-chariot-background-primary border-chariot-stroke/50 flex items-center gap-3 rounded-md border p-[10px_12px]'
          : 'bg-chariot-background-secondary border-chariot-stroke flex items-center gap-3 rounded-md border p-[10px_12px]'
      }
    >
      <div className="flex size-4 flex-shrink-0 items-center justify-center">
        <JobStatusIcon status={job.status} />
      </div>
      <div className="min-w-0 flex-1">
        <div
          className={
            isDone
              ? 'text-chariot-text-secondary truncate text-[13px] font-medium'
              : 'text-chariot-text-primary truncate text-[13px] font-medium'
          }
        >
          {job.source || job.capability || 'Security Scan'}
        </div>
        {target && (
          <div className="text-chariot-text-secondary truncate text-xs">
            Target: <span className="font-mono text-[11px]">{target}</span>
          </div>
        )}
      </div>
      <div className="flex flex-shrink-0 flex-col items-end gap-1">
        <JobStatusChip status={job.status} />
        {duration && (
          <span className="text-chariot-text-tertiary font-mono text-[10px]">
            {duration}
          </span>
        )}
      </div>
    </div>
  );
}

interface SegmentProps {
  title: string;
  count: number;
  variant: 'active' | 'done';
  children: React.ReactNode;
}

function Segment({ title, count, variant, children }: SegmentProps) {
  return (
    <>
      <div className="mt-0 mb-2 ml-0.5 flex items-center gap-2">
        <span className="text-chariot-text-secondary text-[11px] font-semibold tracking-wide uppercase">
          {title}
        </span>
        <span
          className={
            variant === 'active'
              ? 'bg-chariot-brand-primary text-chariot-text-primary inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full px-1.5 text-[10px] font-semibold'
              : 'bg-chariot-background-secondary text-chariot-text-tertiary inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full px-1.5 text-[10px] font-semibold'
          }
        >
          {count}
        </span>
        <span className="bg-chariot-stroke/50 h-px flex-1" />
      </div>
      <div className="mb-6 flex flex-col gap-2">{children}</div>
    </>
  );
}

/**
 * ConversationJobs — persistent segmented ledger of every background
 * capability job for the active conversation. Rendered inside the
 * TraceDrawer "Jobs" tab.
 *
 * Sourced from the SAME tenant-partition poll as the Stop-button signal
 * (`useConversationActiveJobs`); this is a display-only transform over that
 * poll's `jobs` array — it adds no second poll and never sends `user=true`.
 *
 * Splits jobs into two labeled common-region segments:
 *  - Active    (JQ / JR prefixes — queued + running)
 *  - Completed (JP / JF — pass + fail)
 * An empty segment is omitted entirely, so the tab is never blank while any
 * job exists, and the "all done" state shows only the Completed segment.
 */
function ConversationJobs({ jobs, isLoading }: ConversationJobsProps) {
  if (isLoading || !jobs || !Array.isArray(jobs)) {
    return null;
  }

  const activeJobs = jobs.filter(job => isActive(job.status));
  const completedJobs = jobs.filter(
    job => isPass(job.status) || isFail(job.status)
  );

  // Nothing to show (only unknown/partial statuses, or no jobs at all).
  if (activeJobs.length === 0 && completedJobs.length === 0) {
    return null;
  }

  return (
    <div className="mb-2">
      {activeJobs.length > 0 && (
        <Segment title="Active" count={activeJobs.length} variant="active">
          {activeJobs.map((job, index) => (
            <JobCard
              key={job.key || `active-${index}`}
              job={job}
              bucket="active"
            />
          ))}
        </Segment>
      )}

      {completedJobs.length > 0 && (
        <Segment title="Completed" count={completedJobs.length} variant="done">
          {completedJobs.map((job, index) => (
            <JobCard
              key={job.key || `done-${index}`}
              job={job}
              bucket="completed"
            />
          ))}
        </Segment>
      )}

      {activeJobs.length > 0 && (
        <div className="border-chariot-stroke/50 text-chariot-text-secondary mt-1 flex items-center gap-2 border-t pt-3 text-[11px]">
          <span className="bg-chariot-brand-primary size-2 animate-pulse rounded-full" />
          <span>Results are summarized automatically as scans complete.</span>
        </div>
      )}
    </div>
  );
}

export default ConversationJobs;
